import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useEffect, useState } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, isInitialized, initialize } = useAuthStore();
  const location = useLocation();
  const [retryCount, setRetryCount] = useState(0);

  // Initialize auth on first load - only once
  useEffect(() => {
    if (!isInitialized && !isLoading) {
      console.log('🔒 ProtectedRoute: Initializing auth...');
      initialize();
    }
  }, [isInitialized, isLoading, initialize]);

  // Retry initialization if it failed due to network issues
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isLoading && retryCount < 2) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        console.log('🔄 ProtectedRoute: Retrying auth initialization...');
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          initialize();
        }, 1000); // Wait 1 second before retry
      }
    }
  }, [isInitialized, isAuthenticated, isLoading, retryCount, initialize]);

  // Debug logging
  console.log('🔒 ProtectedRoute check:', { 
    isLoading, 
    isAuthenticated, 
    hasUser: !!user,
    isInitialized,
    retryCount,
    hasToken: !!localStorage.getItem('auth_token'),
    path: location.pathname,
    userRole: user?.role
  });

  // Show loading while initializing or loading
  if (!isInitialized || isLoading) {
    console.log('⏳ ProtectedRoute: Showing loading state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-2">Retry attempt {retryCount}/2</p>
          )}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated and no token available
  if (!isAuthenticated || !user) {
    const hasToken = !!localStorage.getItem('auth_token');
    
    // If we have a token but auth failed, and we haven't exceeded retry limit, show loading
    if (hasToken && retryCount < 2 && isInitialized) {
      console.log('⏳ ProtectedRoute: Has token but not authenticated, retrying...');
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Reconnecting...</p>
            <p className="text-sm text-gray-500 mt-2">Retry {retryCount + 1}/2</p>
          </div>
        </div>
      );
    }
    
    // No token or exceeded retry limit - redirect to login
    console.log('🚫 ProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Render protected content
  console.log('✅ ProtectedRoute: Authenticated, rendering protected content');
  return <>{children}</>;
}